import { useState } from "react";
import type { Demo } from "../../shared/types";

export interface LeadValues { email: string; name: string; company: string }

interface Props {
  demo: Demo;
  title: string;
  initial: Partial<LeadValues>;
  onSubmit: (v: LeadValues) => Promise<void>;
  onSkip?: () => void;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** The lead gate: email always, name and company when the demo asks for them. */
export default function LeadForm({ demo, title, initial, onSubmit, onSkip }: Props) {
  const [v, setV] = useState<LeadValues>({ email: initial.email ?? "", name: initial.name ?? "", company: initial.company ?? "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = EMAIL.test(v.email.trim());

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ok) { setError("Enter a valid work email."); return; }
    setBusy(true);
    setError(null);
    try { await onSubmit(v); } catch (err: any) { setError(err.message || "Something went wrong. Try again."); setBusy(false); }
  };

  return (
    <form className="card-center lead-form" onSubmit={submit} noValidate>
      <div className="stack-s">
        <h2>{title || "Where should we send a copy of this tour?"}</h2>
        <p className="muted small">We'll only use this to follow up about this demo.</p>
      </div>
      <div className="stack">
        <label className="field">
          <span>Work email</span>
          <input type="email" autoFocus autoComplete="email" inputMode="email" required value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} />
        </label>
        {(demo.leadAskName || demo.leadAskCompany) && (
          <div className="grid-2">
            {demo.leadAskName && (
              <label className="field"><span>Name <span className="hint">Optional</span></span>
                <input type="text" autoComplete="name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
              </label>
            )}
            {demo.leadAskCompany && (
              <label className="field"><span>Company <span className="hint">Optional</span></span>
                <input type="text" autoComplete="organization" value={v.company} onChange={(e) => setV({ ...v, company: e.target.value })} />
              </label>
            )}
          </div>
        )}
      </div>
      {error && <div className="error-box">{error}</div>}
      <div className="row">
        <span className="spacer" />
        {onSkip && <button type="button" className="btn btn-ghost" onClick={onSkip}>Skip</button>}
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Saving…" : "Continue the tour"}</button>
      </div>
    </form>
  );
}
